import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SplitText from "@/components/TitleSplitEffect";

interface NavItem {
    name: string;
    href: string;
    description?: string;
    items?: NavItem[];
}

interface FullScreenMenuProps {
    items: NavItem[];
    isOpen: boolean;
    onClose: () => void;
}

const menuVariants = {
    closed: {
        opacity: 0,
        scale: 0.98,
        transition: {
            duration: 0.4,
            ease: [0.32, 0.72, 0, 1] as const,
        },
    },
    open: {
        opacity: 1,
        scale: 1,
        transition: {
            duration: 0.5,
            ease: [0.32, 0.72, 0, 1] as const,
        },
    },
    exit: {
        opacity: 0,
        scale: 0.98,
        transition: {
            duration: 0.3,
            ease: [0.32, 0.72, 0, 1] as const,
        },
    },
};

const containerVariants = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.05,
            delayChildren: 0.1,
        },
    },
};

const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    show: {
        y: 0,
        opacity: 1,
        transition: {
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1] as const,
        },
    },
};

export function FullScreenMenu({ items, isOpen, onClose }: FullScreenMenuProps) {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen) {
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => {
            document.body.style.overflow = "unset";
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, onClose]);

    return (
        <AnimatePresence mode="wait">
            {isOpen && (
                <motion.div
                    initial="closed"
                    animate="open"
                    exit="exit"
                    variants={menuVariants}
                    className="fixed inset-0 z-[100] flex flex-col bg-white/98 backdrop-blur-xl text-slate-900 h-[100dvh]"
                >
                    {/* Header */}
                    <div className="container mx-auto px-6 pt-6 flex justify-between items-center">
                        <div onClick={onClose}>
                            <SplitText
                                mainText="Quizzler"
                                subText="Test yourself"
                                href="/"
                                className="text-slate-900 font-medium"
                            />
                        </div>
                        <button
                            onClick={onClose}
                            className="group p-2 rounded-full hover:bg-slate-100 transition-colors"
                            aria-label="Close menu"
                        >
                            <X className="w-8 h-8 text-slate-900 group-hover:rotate-90 transition-transform duration-300" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto py-12 md:py-20">
                        <div className="container mx-auto px-6">
                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                animate="show"
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-20"
                            >
                                {items.map((category, idx) => (
                                    <motion.div key={idx} variants={itemVariants} className="space-y-6">
                                        <Link
                                            to={category.href}
                                            onClick={onClose}
                                            className="inline-block text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 hover:text-blue-600 transition-colors !no-underline"
                                        >
                                            {category.name}
                                        </Link>

                                        {category.items && (
                                            <ul className="space-y-4 pt-2">
                                                {category.items.map((item, itemIdx) => (
                                                    <li key={itemIdx}>
                                                        <Link
                                                            to={item.href}
                                                            onClick={onClose}
                                                            className="group block py-1 transition-all duration-200 !no-underline"
                                                        >
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <div className="text-base font-medium text-slate-700 group-hover:text-blue-600 transition-colors">
                                                                    {item.name}
                                                                </div>
                                                                <ArrowRight className="w-4 h-4 text-blue-600 transform -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300 ease-out" />
                                                            </div>
                                                            {item.description && (
                                                                <p className="text-sm text-slate-500 group-hover:text-slate-600 line-clamp-2 leading-relaxed">
                                                                    {item.description}
                                                                </p>
                                                            )}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </div>

                    {/* Menu Footer */}
                    <div className="container mx-auto px-6 pb-8 pt-4 border-t border-slate-100">
                        <div className="flex flex-wrap gap-6 text-sm text-slate-500">
                            <Link to="/about" onClick={onClose} className="hover:text-slate-900 transition-colors">
                                About
                            </Link>
                            <Link to="/privacy" onClick={onClose} className="hover:text-slate-900 transition-colors">
                                Privacy Policy
                            </Link>
                            <Link to="/terms" onClick={onClose} className="hover:text-slate-900 transition-colors">
                                Terms of Service
                            </Link>
                            <div className="ml-auto text-slate-400">
                                © {new Date().getFullYear()} Quizzler
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
